import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import MarkdownToJsx from 'markdown-to-jsx'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { slugify } from '../../helpers/guide'
import { APP_BAR_HEIGHT, FONT, MARKDOWN, RADIUS } from '../../theme/tokens'

/**
 * True for a link inside the app: a path from the root (`/bulk-send`,
 * `/guide#pipelines`) or a fragment (`#variables`). A protocol-relative
 * `//host` is another site, so it is not one.
 *
 * @param {string} href
 */
export const isInAppHref = (href) =>
    typeof href === 'string' &&
    (/^\/(?![/\\])/.test(href) || href.startsWith('#'))

/** The plain text of rendered children (a heading's words, for its actions). */
const textOf = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean')
        return ''
    if (typeof node === 'string' || typeof node === 'number') return `${node}`
    if (Array.isArray(node)) return node.map(textOf).join('')
    return textOf(node.props?.children)
}

const HEADING_VARIANT = {
    1: 'h4',
    2: 'h5',
    3: 'h6',
    4: 'subtitle1',
    5: 'subtitle2',
    6: 'subtitle2',
}

/**
 * A heading with its anchor (from slugify, so `/guide#bulk-send` lands on
 * it below the fixed header) and, when given, the page's actions for it
 * (copy link, ask BhanzuBot).
 */
const Heading = ({ level, id, children, actions, dense }) => (
    <Typography
        id={id}
        component={`h${level}`}
        variant={dense ? 'subtitle2' : HEADING_VARIANT[level]}
        sx={{
            scrollMarginTop: `${APP_BAR_HEIGHT + 8}px`,
            color: MARKDOWN.heading,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: dense ? 1 : level <= 2 ? 4 : 2.5,
            mb: dense ? 0.5 : 1,
            '&:first-of-type': { mt: 0 },
        }}
    >
        <span>{children}</span>
        {actions ? actions({ level, id, text: textOf(children) }) : null}
    </Typography>
)

Heading.propTypes = {
    level: PropTypes.number.isRequired,
    id: PropTypes.string,
    children: PropTypes.node,
    actions: PropTypes.func,
    dense: PropTypes.bool,
}

/**
 * A link: inside the app it navigates with the router (and tells
 * `onNavigate`); anything else is shown as its words only. The guide and
 * BhanzuBot's answers are not trusted to send anyone off the app.
 */
const MarkdownLink = ({ href, children, onNavigate }) => {
    if (!isInAppHref(href)) return <span>{children}</span>
    return (
        <Link
            component={RouterLink}
            to={href}
            underline="hover"
            onClick={() => onNavigate && onNavigate(href)}
            sx={{ color: MARKDOWN.link, fontWeight: 500 }}
        >
            {children}
        </Link>
    )
}

MarkdownLink.propTypes = {
    href: PropTypes.string,
    children: PropTypes.node,
    onNavigate: PropTypes.func,
}

/** An image is never loaded from markdown: its description stands in. */
const NoImage = ({ alt }) => (alt ? <span>{alt}</span> : null)
NoImage.propTypes = { alt: PropTypes.string }

const bodySx = (dense) => ({
    color: MARKDOWN.text,
    typography: dense ? 'body2' : 'body1',
    lineHeight: 1.6,
})

const Paragraph = ({ children, dense }) => (
    <Typography
        component="p"
        variant="body2"
        sx={{ ...bodySx(dense), mt: 0, mb: dense ? 1 : 1.5 }}
    >
        {children}
    </Typography>
)
Paragraph.propTypes = { children: PropTypes.node, dense: PropTypes.bool }

const List = ({ ordered, children, dense, start }) => (
    <Box
        component={ordered ? 'ol' : 'ul'}
        start={start}
        sx={{ ...bodySx(dense), pl: 2.5, mt: 0, mb: dense ? 1 : 1.5 }}
    >
        {children}
    </Box>
)
List.propTypes = {
    ordered: PropTypes.bool,
    children: PropTypes.node,
    dense: PropTypes.bool,
    start: PropTypes.number,
}

const ListItem = ({ children }) => (
    <Box component="li" sx={{ mb: 0.5 }}>
        {children}
    </Box>
)
ListItem.propTypes = { children: PropTypes.node }

const InlineCode = ({ children }) => (
    <Box
        component="code"
        sx={{
            fontFamily: FONT.mono,
            color: MARKDOWN.code,
            backgroundColor: MARKDOWN.codeBg,
            borderRadius: `${RADIUS.xs}px`,
            px: 0.5,
            py: '1px',
            overflowWrap: 'anywhere',
        }}
    >
        {children}
    </Box>
)
InlineCode.propTypes = { children: PropTypes.node }

const CodeBlock = ({ children }) => (
    <Box
        component="pre"
        sx={{
            m: 0,
            mb: 1.5,
            p: 1.25,
            overflowX: 'auto',
            borderRadius: `${RADIUS.sm}px`,
            backgroundColor: MARKDOWN.codeBg,
            '& code': { backgroundColor: 'transparent', p: 0 },
        }}
    >
        {children}
    </Box>
)
CodeBlock.propTypes = { children: PropTypes.node }

const MarkdownTable = ({ children }) => (
    <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
        <Table size="small" sx={{ minWidth: 320 }}>
            {children}
        </Table>
    </Box>
)
MarkdownTable.propTypes = { children: PropTypes.node }

/**
 * A table cell. Words never break inside it: the chat bubble's
 * `overflowWrap: anywhere` let columns shrink to one letter, so a holiday
 * table in the 380px panel read "Diwal i_202 6" and never scrolled (dev
 * walkthrough, 2026-10-20). In chat (`dense`) a cell keeps to one line and a
 * wide table scrolls sideways in its box; on the Guide page cells wrap
 * between words.
 */
const Cell = ({ children, style, head, dense }) => (
    <TableCell
        component={head ? 'th' : 'td'}
        variant={head ? 'head' : 'body'}
        style={style}
        sx={{
            typography: head ? 'overline' : 'body2',
            borderColor: MARKDOWN.rule,
            overflowWrap: 'normal',
            wordBreak: 'normal',
            whiteSpace: dense ? 'nowrap' : 'normal',
        }}
    >
        {children}
    </TableCell>
)
Cell.propTypes = {
    children: PropTypes.node,
    style: PropTypes.object,
    head: PropTypes.bool,
    dense: PropTypes.bool,
}

const Quote = ({ children }) => (
    <Box
        component="blockquote"
        sx={{
            m: 0,
            mb: 1.5,
            pl: 1.5,
            borderLeft: `3px solid ${MARKDOWN.rule}`,
        }}
    >
        {children}
    </Box>
)
Quote.propTypes = { children: PropTypes.node }

/**
 * Markdown shown safely: the Comms Guide and BhanzuBot's answers, both
 * treated as untrusted text.
 *
 * - Raw HTML is never parsed: `<script>` or `<img onerror>` shows as text.
 * - Images are not loaded; links leave the app never (only in-app paths
 *   are links, through the router; `onNavigate` hears each click).
 * - Headings get the guide's anchors (helpers/guide slugify) and, through
 *   `headingActions({level, id, text})`, a page's own buttons beside them.
 * - Tables are small MUI tables that scroll sideways on a phone; colours
 *   come from the MARKDOWN tokens and the type from the theme's scale.
 * - `dense` is the chat size: smaller headings and tighter spacing.
 */
const Markdown = ({ children, onNavigate, headingActions, dense = false }) => {
    const options = useMemo(() => {
        const heading = (level) => ({
            component: Heading,
            props: { level, actions: headingActions, dense },
        })
        return {
            disableParsingRawHTML: true,
            forceBlock: true,
            forceWrapper: true,
            wrapper: 'div',
            slugify,
            overrides: {
                h1: heading(1),
                h2: heading(2),
                h3: heading(3),
                h4: heading(4),
                h5: heading(5),
                h6: heading(6),
                a: { component: MarkdownLink, props: { onNavigate } },
                img: { component: NoImage },
                p: { component: Paragraph, props: { dense } },
                ul: { component: List, props: { dense } },
                ol: { component: List, props: { dense, ordered: true } },
                li: { component: ListItem },
                code: { component: InlineCode },
                pre: { component: CodeBlock },
                table: { component: MarkdownTable },
                thead: { component: TableHead },
                tbody: { component: TableBody },
                tr: { component: TableRow },
                th: { component: Cell, props: { head: true, dense } },
                td: { component: Cell, props: { dense } },
                blockquote: { component: Quote },
                hr: { component: Divider, props: { sx: { my: 2 } } },
            },
        }
    }, [onNavigate, headingActions, dense])

    return (
        <MarkdownToJsx options={options}>
            {typeof children === 'string' ? children : ''}
        </MarkdownToJsx>
    )
}

Markdown.propTypes = {
    /** The markdown text. */
    children: PropTypes.string,
    /** Called with the href of an in-app link when it is clicked. */
    onNavigate: PropTypes.func,
    /** Renders a heading's buttons: ({level, id, text}) => node. */
    headingActions: PropTypes.func,
    dense: PropTypes.bool,
}

export default Markdown
